import { z } from 'zod';

/**
 * Checklist dos primeiros 30 dias (GS4). Para todos os streamers, sem estado
 * nem rotulo proprio (RN-GS10). Cada item tem quem o marca: o streamer, o
 * padrinho (ou o Stream Staff) ou a plataforma sozinha.
 */

const datetimeSchema = z.string().datetime({ offset: true });

/** Dias de referencia da checklist; o padrinho pode prolongar (RN-GS5). */
export const CHECKLIST_DAYS = 30;
/** Transmissoes e minutos das regras automaticas. */
export const CHECKLIST_STREAMS = 4;
export const CHECKLIST_MINUTES = 6 * 60;

export const checklistSectionSchema = z.enum([
  'welcome',
  'padrinho',
  'brand',
  'technical',
  'streaming',
  'closing',
]);
export type ChecklistSection = z.infer<typeof checklistSectionSchema>;

export const checklistMarkedBySchema = z.enum(['streamer', 'padrinho', 'auto']);
export type ChecklistMarkedBy = z.infer<typeof checklistMarkedBySchema>;

export const checklistRuleSchema = z.enum([
  'twitch',
  'wall_post',
  'padrinho',
  'intro',
  'streams',
  'hours',
]);
export type ChecklistRule = z.infer<typeof checklistRuleSchema>;

/** Um item da lista do programa (Stream Staff). */
export const checklistItemSchema = z.object({
  id: z.string().uuid(),
  section: checklistSectionSchema,
  position: z.number().int(),
  title: z.string(),
  help: z.string(),
  required: z.boolean(),
  markedBy: checklistMarkedBySchema,
  rule: checklistRuleSchema.nullable(),
  active: z.boolean(),
});
export type ChecklistItem = z.infer<typeof checklistItemSchema>;

const titleSchema = z.string().trim().min(3, 'escreve o item').max(200);

/** Novo item manual (os automaticos ja existem e nao se criam). */
export const checklistItemInputSchema = z.object({
  section: checklistSectionSchema,
  title: titleSchema,
  help: z.string().trim().max(500).default(''),
  required: z.boolean().default(true),
  markedBy: z.enum(['streamer', 'padrinho']),
});

/** Mudar um item. Num automatico so contam `active`, `required` e o texto. */
export const checklistItemUpdateSchema = z.object({
  title: titleSchema.optional(),
  help: z.string().trim().max(500).optional(),
  required: z.boolean().optional(),
  markedBy: z.enum(['streamer', 'padrinho']).optional(),
  position: z.number().int().min(0).max(999).optional(),
  active: z.boolean().optional(),
});

/** Um item na checklist de um streamer. */
export const checklistEntrySchema = z.object({
  itemId: z.string().uuid(),
  section: checklistSectionSchema,
  title: z.string(),
  help: z.string(),
  required: z.boolean(),
  markedBy: checklistMarkedBySchema,
  done: z.boolean(),
  notApplicable: z.boolean(),
  /** Quem marcou e quando (os automaticos nao guardam). */
  doneBy: z.string().nullable(),
  doneAt: datetimeSchema.nullable(),
  note: z.string().nullable(),
  /** Nos automaticos de contagem: "2 de 4". */
  progress: z.string().nullable(),
});
export type ChecklistEntry = z.infer<typeof checklistEntrySchema>;

export const streamerChecklistSchema = z.object({
  startedAt: datetimeSchema,
  dueOn: datetimeSchema,
  extraDays: z.number().int(),
  closedAt: datetimeSchema.nullable(),
  closedBy: z.string().nullable(),
  /** Obrigatorios feitos ou "Nao se aplica", de quantos. */
  requiredDone: z.number().int(),
  requiredTotal: z.number().int(),
  done: z.number().int(),
  total: z.number().int(),
  entries: z.array(checklistEntrySchema),
});
export type StreamerChecklist = z.infer<typeof streamerChecklistSchema>;

/** O padrinho (ou o Stream Staff) marca um item, ou poe-no em "Nao se aplica" com motivo. */
export const checklistMarkInputSchema = z
  .object({
    notApplicable: z.boolean().default(false),
    note: z
      .string()
      .trim()
      .max(500)
      .transform((s) => (s === '' ? null : s))
      .nullable()
      .default(null),
  })
  .superRefine((v, ctx) => {
    if (v.notApplicable && (v.note === null || v.note.length < 3)) {
      ctx.addIssue({ code: 'custom', path: ['note'], message: 'diz porque nao se aplica' });
    }
  });

/**
 * Uma checklist ainda aberta, para o Resumo da gestao (arrumar a gestao):
 * quem esta nos primeiros 30 dias, com que padrinho, quanto falta e ate quando.
 */
export const openChecklistSchema = z.object({
  streamerId: z.string().uuid(),
  accountId: z.string().uuid(),
  displayName: z.string(),
  /** Login do padrinho; `null` = sem padrinho. */
  padrinho: z.string().nullable(),
  done: z.number().int(),
  total: z.number().int(),
  requiredDone: z.number().int(),
  requiredTotal: z.number().int(),
  dueOn: datetimeSchema,
});
export type OpenChecklist = z.infer<typeof openChecklistSchema>;
export const openChecklistsSchema = z.array(openChecklistSchema);

/** Prolongar a checklist (RN-GS5): mais 15 ou 30 dias. */
export const checklistExtendSchema = z.object({
  days: z.union([z.literal(15), z.literal(30)]),
});
